import React from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import { Form, RangeSlider } from '../../../components';

import css from './PriceFilterForm.module.css';

const DEBOUNCE_WAIT_TIME = 400;
const VALIDATION_DEBOUNCE_TIME = 1500; // Wait 1.5 seconds before auto-correcting values

// Helper function to parse value for min handle
// Value needs to be between slider's minimum value and current maximum value
const parseMin = (min, currentMax) => value => {
  const parsedValue = Number.parseInt(value, 10);
  if (isNaN(parsedValue)) {
    return '';
  }
  return parsedValue < min ? min : parsedValue > currentMax ? currentMax : parsedValue;
};

// Helper function to parse value for max handle
// Value needs to be between slider's max value and current minimum value
const parseMax = (max, currentMin) => value => {
  const parsedValue = Number.parseInt(value, 10);
  if (isNaN(parsedValue)) {
    return '';
  }
  return parsedValue < currentMin ? currentMin : parsedValue > max ? max : parsedValue;
};

/**
 * PriceFilterForm component
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - The ID
 * @param {boolean} [props.liveEdit] - Whether the filter is live editable
 * @param {boolean} [props.showAsPopup] - Whether the filter is shown as a popup
 * @param {Function} props.onChange - The function to change the filter
 * @param {Function} props.onSubmit - The function to submit the filter
 * @param {Function} props.onCancel - The function to cancel the filter
 * @param {Function} props.onClear - The function to clear the filter
 * @param {boolean} [props.isOpen] - Whether the filter is open
 * @param {Function} props.contentRef - The function to get the content ref
 * @param {Object} props.style - The style
 * @param {number} props.min - The minimum price for the price range filter
 * @param {number} props.max - The maximum price for the price range filter
 * @param {number} props.step - The step for the price range filter
 * @returns {JSX.Element}
 */
const PriceFilterForm = props => {
  const intl = useIntl();
  const { liveEdit = false, onChange, onSubmit, onCancel, onClear, ...rest } = props;

  // Keep track of the last valid slider range to avoid jumping while user types
  const initialMinBound = rest.min ?? 0;
  const initialMaxBound = rest.max ?? 0;
  const lastValidRangeRef = React.useRef([initialMinBound, initialMaxBound]);

  // Track which field was last changed by the user
  const lastChangedFieldRef = React.useRef(null);

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  const handleChange = debounce(
    formState => {
      if (formState.dirty) {
        const { minPrice, maxPrice, ...restValues } = formState.values;

        // Parse and clamp values
        const minBound = rest.min ?? 0;
        const maxBound = rest.max ?? 0;
        const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

        const parsedMin = minPrice === '' || minPrice == null ? minBound : Number(minPrice);
        const parsedMax = maxPrice === '' || maxPrice == null ? maxBound : Number(maxPrice);

        const safeMin = Number.isFinite(parsedMin) ? clamp(parsedMin, minBound, maxBound) : minBound;
        const safeMax = Number.isFinite(parsedMax) ? clamp(parsedMax, minBound, maxBound) : maxBound;

        // Clamp values: if max < min set max = min; if min > max set min = max
        let finalMin = safeMin;
        let finalMax = safeMax;

        if (finalMax < finalMin) {
          finalMax = finalMin;
        } else if (finalMin > finalMax) {
          finalMin = finalMax;
        }

        onChange({
          minPrice: finalMin,
          maxPrice: finalMax,
          ...restValues,
        });
      }
    },
    DEBOUNCE_WAIT_TIME,
    { leading: false, trailing: true }
  );

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    const minBound = rest.min ?? 0;
    const maxBound = rest.max ?? 0;

    const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

    // Parse numbers loosely: empty strings fall back to bounds
    const parsedMin = minPrice === '' || minPrice == null ? minBound : Number(minPrice);
    const parsedMax = maxPrice === '' || maxPrice == null ? maxBound : Number(maxPrice);

    const safeMin = Number.isFinite(parsedMin) ? clamp(parsedMin, minBound, maxBound) : minBound;
    const safeMax = Number.isFinite(parsedMax) ? clamp(parsedMax, minBound, maxBound) : maxBound;

    // Clamp values: if max < min set max = min; if min > max set min = max
    let finalMin = safeMin;
    let finalMax = safeMax;

    if (finalMax < finalMin) {
      finalMax = finalMin;
    } else if (finalMin > finalMax) {
      finalMin = finalMax;
    }

    return onSubmit({
      minPrice: finalMin,
      maxPrice: finalMax,
      ...restValues,
    });
  };

  const formCallbacks = liveEdit
    ? { onSubmit: () => null }
    : { onSubmit: handleSubmit, onCancel, onClear };
  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      render={formRenderProps => {
        const {
          form,
          handleSubmit,
          id,
          showAsPopup = false,
          onClear,
          onCancel,
          isOpen = false,
          isInSideBar = false,
          contentRef,
          style,
          intl,
          values,
          min = 0,
          max = 1000,
          step = 1,
          children,
        } = formRenderProps;
        const { minPrice: minPriceRaw, maxPrice: maxPriceRaw } = values;

        // Track previous values to detect which field changed
        const prevValuesRef = React.useRef({ minPrice: minPriceRaw, maxPrice: maxPriceRaw });

        // Effect to track which field changed
        React.useEffect(() => {
          const prevMin = prevValuesRef.current.minPrice;
          const prevMax = prevValuesRef.current.maxPrice;

          if (minPriceRaw !== prevMin && maxPriceRaw === prevMax) {
            lastChangedFieldRef.current = 'minPrice';
          } else if (maxPriceRaw !== prevMax && minPriceRaw === prevMin) {
            lastChangedFieldRef.current = 'maxPrice';
          } else if (minPriceRaw !== prevMin && maxPriceRaw !== prevMax) {
            // Both changed (e.g., from slider) - don't update lastChanged
            // This keeps the last manual input field tracked
          }

          // Update previous values
          prevValuesRef.current = { minPrice: minPriceRaw, maxPrice: maxPriceRaw };
        }, [minPriceRaw, maxPriceRaw]);

        // Debounced validation to auto-correct invalid values
        const validateAndCorrectValuesRef = React.useRef(null);
        React.useEffect(() => {
          // Clear any existing timeout
          if (validateAndCorrectValuesRef.current) {
            clearTimeout(validateAndCorrectValuesRef.current);
          }

          // Set up new validation timeout
          validateAndCorrectValuesRef.current = setTimeout(() => {
            const parseNum = v => {
              if (v === '' || v == null) return null;
              const n = Number(v);
              return Number.isFinite(n) ? n : null;
            };

            const parsedMin = parseNum(minPriceRaw);
            const parsedMax = parseNum(maxPriceRaw);

            // Only validate if both values exist
            if (parsedMin != null && parsedMax != null) {
              let needsCorrection = false;
              let correctedMin = parsedMin;
              let correctedMax = parsedMax;

              // Determine which field to correct based on which was last changed
              const lastChanged = lastChangedFieldRef.current;

              if (parsedMax < parsedMin) {
                // If user just changed max (and it's too low), set max = min
                if (lastChanged === 'maxPrice') {
                  correctedMax = parsedMin;
                  needsCorrection = true;
                }
                // If user just changed min (and it's too high), set min = max
                else if (lastChanged === 'minPrice') {
                  correctedMin = parsedMax;
                  needsCorrection = true;
                }
                // Default: if we don't know which changed, set max = min (original behavior)
                else {
                  correctedMax = parsedMin;
                  needsCorrection = true;
                }
              }

              // Apply corrections if needed
              if (needsCorrection) {
                form.batch(() => {
                  form.change('minPrice', correctedMin);
                  form.change('maxPrice', correctedMax);
                });
              }
            }
          }, VALIDATION_DEBOUNCE_TIME);

          // Cleanup on unmount or when values change
          return () => {
            if (validateAndCorrectValuesRef.current) {
              clearTimeout(validateAndCorrectValuesRef.current);
            }
          };
        }, [minPriceRaw, maxPriceRaw, form]);

        const parseNum = v => {
          if (v === '' || v == null) return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };
        const parsedMin = parseNum(minPriceRaw);
        const parsedMax = parseNum(maxPriceRaw);
        const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

        const bothValidAndOrdered =
          parsedMin != null && parsedMax != null && parsedMin <= parsedMax;

        // Update last valid range when inputs are valid and ordered
        if (bothValidAndOrdered) {
          const clampedMin = clamp(parsedMin, min, max);
          const clampedMax = clamp(parsedMax, min, max);
          lastValidRangeRef.current = [clampedMin, clampedMax];
        }

        // Use current valid values for slider if available; otherwise freeze at last valid
        const [lastValidMin, lastValidMax] = lastValidRangeRef.current;
        const minPrice = bothValidAndOrdered ? clamp(parsedMin, min, max) : lastValidMin;
        const maxPrice = bothValidAndOrdered ? clamp(parsedMax, min, max) : lastValidMax;

        const handleCancel = () => {
          // reset the final form to initialValues
          form.reset();
          onCancel();
        };

        const clear = intl.formatMessage({ id: 'PriceFilterForm.clear' });
        const cancel = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
        const submit = intl.formatMessage({ id: 'PriceFilterForm.submit' });

        const classes = classNames(css.root, {
          [css.popup]: showAsPopup,
          [css.isOpenAsPopup]: showAsPopup && isOpen,
          [css.plain]: !showAsPopup,
          [css.isOpen]: !showAsPopup && isOpen,
        });

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            contentRef={contentRef}
            style={{ minWidth: '300px', ...style }}
          >
            <div className={css.contentWrapper}>
              <div className={css.inputsWrapper}>
                <Field
                  className={classNames(css.minPrice, { [css.priceInSidebar]: isInSideBar })}
                  id={`${id}.minPrice`}
                  name="minPrice"
                  component="input"
                  type="number"
                  placeholder={min}
                  min={min}
                  max={max}
                  step={step}
                />
                <span className={css.priceSeparator}>-</span>
                <Field
                  className={classNames(css.maxPrice, { [css.priceInSidebar]: isInSideBar })}
                  id={`${id}.maxPrice`}
                  name="maxPrice"
                  component="input"
                  type="number"
                  placeholder={max}
                  min={min}
                  max={max}
                  step={step}
                />
              </div>
            </div>

            <div className={css.sliderWrapper}>
              <RangeSlider
                min={min}
                max={max}
                step={step}
                handles={[minPrice, maxPrice]}
                onChange={handles => {
                  form.change('minPrice', handles[0]);
                  form.change('maxPrice', handles[1]);
                }}
              />
            </div>

            {liveEdit ? (
              <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
            ) : (
              <div className={css.buttonsWrapper}>
                {/*<button className={css.clearButton} type="button" onClick={onClear}>*/}
                {/*  {clear}*/}
                {/*</button>*/}
                <button className={css.cancelButton} type="button" onClick={handleCancel}>
                  {cancel}
                </button>
                <button className={css.submitButton} type="submit">
                  {submit}
                </button>
              </div>
            )}
            {children}
          </Form>
        );
      }}
    />
  );
};

export default PriceFilterForm;
